import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';

// const API_URL = "http://localhost:3000";
const API_URL = "https://ai-sales-agent-two.vercel.app";

const UpdateSitePrices = () => {
  const [site, setSite] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [newPrices, setNewPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // const [sites, setSites] = useState(['sitea', 'sitew', 'siter']);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [newSiteDialogOpen, setNewSiteDialogOpen] = useState(false);
  const [newProductDialogOpen, setNewProductDialogOpen] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newProduct, setNewProduct] = useState({
    mixCode: '',
    type: '',
    strength: '',
    price: ''
  });

  // Filter products based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(product =>
        product.mixCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.strength.toString().includes(searchTerm)
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    if (!site) return;
    setLoading(true);
    setAlert(null);
    try {
      const response = await fetch(`${API_URL}/admin/site-products?site=${site.replace('site', '')}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data.products);
      setFilteredProducts(data.products);
      setNewPrices({});
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.message || 'Failed to fetch products',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    setSitesLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/sites`, {
        headers: {
          'x-admin-password': 'admin1@'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSites(data.sites);
    } catch (err) {
      console.error('Failed to fetch sites:', err);
      setAlert({
        type: 'error',
        message: 'Failed to load sites. Please check your connection.'
      });
    } finally {
      setSitesLoading(false);
    }
  };
  const handlePriceInput = (mixCode, value) => {
    setNewPrices((prev) => ({ ...prev, [mixCode]: value }));
  };

  const updatePrice = async (mixCode) => {
    const newPrice = parseFloat(newPrices[mixCode]);
    if (isNaN(newPrice) || newPrice <= 0) {
      setAlert({ type: 'error', message: `Invalid price for ${mixCode}` });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/update-price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          site: site.replace('site', ''),
          mixCode,
          newPrice,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update price for ${mixCode}`);
      }

      const data = await response.json();
      setAlert({ type: 'success', message: data.message });
      fetchProducts();
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.message || `Failed to update price for ${mixCode}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    // Enhanced validation
    const updates = Object.entries(newPrices)
      .filter(([mixCode, value]) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          console.warn(`Invalid number for ${mixCode}: ${value}`);
          return false;
        }
        if (numValue <= 0) {
          console.warn(`Price must be positive for ${mixCode}`);
          return false;
        }
        return true;
      })
      .map(([mixCode, value]) => ({
        mixCode,
        newPrice: parseFloat(value)
      }));

    if (updates.length === 0) {
      setAlert({ type: 'error', message: 'No valid price updates to submit' });
      return;
    }

    console.log('Prepared updates:', updates); // Debug log

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/bulk-update-prices`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': 'admin1@'
        },
        body: JSON.stringify({
          site: site.replace('site', ''),
          updates
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Received non-JSON response:', text.substring(0, 100));
        throw new Error('Server returned HTML instead of JSON');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform bulk update');
      }

      const data = await response.json();
      setAlert({ type: 'success', message: data.message });
      fetchProducts();
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.message || 'Failed to perform bulk update',
      });
    } finally {
      setLoading(false);
    }
  }; // This closing brace was missing

  const addNewSite = async () => {
    if (!newSiteName) {
      setAlert({ type: 'error', message: 'Site name is required' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/add-site`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': 'admin1@'
        },
        body: JSON.stringify({
          siteName: newSiteName
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add new site');
      }

      const data = await response.json();
      setAlert({ type: 'success', message: data.message });
      setNewSiteDialogOpen(false);
      setNewSiteName('');
      fetchSites();
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.message || 'Failed to add new site',
      });
    } finally {
      setLoading(false);
    }
  };

  const addNewProduct = async () => {
    if (!newProduct.mixCode || !newProduct.type || !newProduct.strength || !newProduct.price) {
      setAlert({ type: 'error', message: 'All fields are required' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/add-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': 'admin1@'
        },
        body: JSON.stringify({
          site: site.replace('site', ''),
          product: {
            mixCode: newProduct.mixCode,
            type: newProduct.type,
            strength: parseFloat(newProduct.strength),
            price: parseFloat(newProduct.price)
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add new product');
      }

      const data = await response.json();
      setAlert({ type: 'success', message: data.message });
      setNewProductDialogOpen(false);
      setNewProduct({
        mixCode: '',
        type: '',
        strength: '',
        price: ''
      });
      fetchProducts();
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.message || 'Failed to add new product',
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSites();
  }, []);
  const deleteSite = async (siteName) => {
  if (!window.confirm(`Are you sure you want to delete ${siteName}?`)) return;

  try {
    setLoading(true);
    const response = await fetch(`${API_URL}/admin/delete-site`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-password': 'admin1@'
      },
      body: JSON.stringify({ siteName })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete site');
    }

    const data = await response.json();
    setAlert({ type: 'success', message: data.message });
    fetchSites(); // Refresh the sites list
  } catch (err) {
    setAlert({
      type: 'error',
      message: err.message || 'Failed to delete site'
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, height: '80vh', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" gutterBottom>
          Admin: Concrete Products Management
        </Typography>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Price Management" />
          <Tab label="Site Management" />
        </Tabs>

        {activeTab === 0 ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                select
                label="Select Site"
                value={site}
                onChange={(e) => setSite(e.target.value)}
                sx={{ minWidth: 200 }}
                disabled={sitesLoading}
              >
                {sitesLoading ? (
                  <MenuItem>Loading sites...</MenuItem>
                ) : (
                  sites.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.toUpperCase()}
                    </MenuItem>
                  ))
                )}
              </TextField>

              <Button
                variant="contained"
                onClick={fetchProducts}
                disabled={loading || !site}
                sx={{ minWidth: 150 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Load Products'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setNewProductDialogOpen(true)}
                disabled={!site}
              >
                Add Product
              </Button>
            </Box>

            {alert && (
              <Alert severity={alert.type} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            )}

            {products.length > 0 && (
              <>
                <TextField
                  label="Search Products"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mix Code</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Strength (MPa)</TableCell>
                        <TableCell>Current Price</TableCell>
                        <TableCell>New Price</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProducts.map((product) => (
                        <TableRow key={product.mixCode}>
                          <TableCell>{product.mixCode}</TableCell>
                          <TableCell>{product.type}</TableCell>
                          <TableCell>{product.strength}</TableCell>
                          <TableCell>R {product.price.toFixed(2)}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={newPrices[product.mixCode] || ''}
                              onChange={(e) => handlePriceInput(product.mixCode, e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">R</InputAdornment>,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              onClick={() => updatePrice(product.mixCode)}
                              disabled={!newPrices[product.mixCode]}
                            >
                              Update
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBulkUpdate}
                    disabled={Object.keys(newPrices).length === 0}
                    sx={{ minWidth: 150 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Bulk Update'}
                  </Button>
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setNewSiteDialogOpen(true)}
              >
                Add New Site
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Site Name</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sites.map((siteItem) => (
                    <TableRow key={siteItem}>
                      <TableCell>{siteItem.toUpperCase()}</TableCell>
                      <TableCell>
                        <Button 
  variant="outlined" 
  color="error"
  onClick={() => deleteSite(siteItem)}
  disabled={loading}
>
  Delete
</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Add New Site Dialog */}
      <Dialog open={newSiteDialogOpen} onClose={() => setNewSiteDialogOpen(false)}>
        <DialogTitle>Add New Site</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Site Name"
            fullWidth
            variant="standard"
            value={newSiteName}
            onChange={(e) => setNewSiteName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSiteDialogOpen(false)}>Cancel</Button>
          <Button onClick={addNewSite} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Add Site'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Product Dialog */}
      <Dialog open={newProductDialogOpen} onClose={() => setNewProductDialogOpen(false)}>
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Mix Code"
            fullWidth
            variant="standard"
            value={newProduct.mixCode}
            onChange={(e) => setNewProduct({ ...newProduct, mixCode: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            variant="standard"
            value={newProduct.type}
            onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Strength (MPa)"
            type="number"
            fullWidth
            variant="standard"
            value={newProduct.strength}
            onChange={(e) => setNewProduct({ ...newProduct, strength: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            variant="standard"
            InputProps={{
              startAdornment: <InputAdornment position="start">R</InputAdornment>,
            }}
            value={newProduct.price}
            onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewProductDialogOpen(false)}>Cancel</Button>
          <Button onClick={addNewProduct} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UpdateSitePrices;